import { axios } from '@/plugins/http-axios';
import { defineStore } from 'pinia';

import { IAction as IAction2 } from '@/types/appTypes';

interface IAction {
  show: boolean;
  title: string;
  comment: string;
  titleComment: string;
  method: string;
  actionId: string;
  appId: string;
  titleButton: string;
  disabled?: boolean;
  skipModal?: boolean;
  requiredComment: boolean;
}

const modelAction: IAction = {
  show: false,
  title: '',
  comment: '',
  titleComment: '',
  method: '_empty',
  actionId: '',
  appId: '',
  titleButton: '',
  disabled: false,
  requiredComment: false,
};

export interface IAccount {
  show: boolean;
  appInn: string;
  appId: string;
}

interface IBaseButton {
  color: string;
  icon: string;
}

interface IButton {
  [key: string]: IBaseButton;
}

interface IExtendButton extends IBaseButton {
  method: string;
}

interface IActionState {
  modalReserveAccount: IAccount;
  modelAction: IAction;
  reserveCurrencyAccount: IAccount;
  currencyTransitAccount: IAccount;
  buttons: IButton;
  isRedirect: boolean;
  action_completed: number;
}

export const useActionsStore = defineStore('actions', {
  state: (): IActionState => ({
    modalReserveAccount: {
      show: false,
      appInn: '',
      appId: '',
    },
    reserveCurrencyAccount: {
      show: false,
      appInn: '',
      appId: '',
    },
    currencyTransitAccount: {
      show: false,
      appInn: '',
      appId: '',
    },
    modelAction: structuredClone(modelAction),
    buttons: {
      OK: {
        color: 'primary',
        icon: 'mdi-briefcase-upload',
      },
      SIGN: {
        color: 'primary',
        icon: 'mdi-briefcase-edit',
      },
      REFUSE: {
        color: 'error',
        icon: 'mdi-briefcase-remove',
      },
      REFUSE_COMMENT: {
        color: 'error',
        icon: 'mdi-briefcase-remove',
      },
      ACQUIRE: {
        color: 'success',
        icon: 'mdi-briefcase-plus',
      },
      PASS: {
        color: 'error',
        icon: 'mdi-briefcase-minus',
      },
      OPEN_ACCOUNT: {
        color: 'success',
        icon: 'mdi-briefcase-check',
      },
      OPEN_ACCOUNT_WITHOUT_INTEGRATION: {
        color: 'success',
        icon: 'mdi-briefcase-check',
      },
      RECHECK: {
        color: 'primary',
        icon: 'mdi-account',
      },
      CHANGE_COMMENT: {
        color: 'primary',
        icon: 'mdi-account',
      },
      CHANGE_RESULT_CHECK: {
        color: 'primary',
        icon: 'mdi-account',
      },
      SIGNING_AFTER_VERIFICATION: {
        color: 'primary',
        icon: 'mdi-briefcase-edit',
      },
      NEXT_STATUS_AFTER_VERIFICATION: {
        color: 'primary',
        icon: 'mdi-briefcase-upload',
      },
    },
    isRedirect: false,
    action_completed: 0,
  }),

  getters: {
    isAvailabilityButtons:
      () =>
      ({ type = '' }: IAction2): boolean => {
        switch (type) {
          case 'RECHECK':
          case 'CHANGE_COMMENT':
          case 'CHANGE_RESULT_CHECK':
          case 'CHANGE_DATE_LIFE':
          case 'ADD_REQUEST':
          case 'REVOKE_REPEAT_REQUIREMENT':
          case 'ADD_REPEAT_REQUIREMENT':
          case 'REVOKE_REQUEST':
            return false;
          default:
            return true;
        }
      },
    getButton:
      (state) =>
      ({ type = '' }: IAction2): IExtendButton => {
        return state.buttons[type]
          ? {
              ...state.buttons[type],
              method: type,
            }
          : { color: 'primary', icon: 'mdi-account', method: '_empty' };
      },
  },

  actions: {
    setActionModal(data: IAction = modelAction) {
      let key: keyof IAction;
      for (key in modelAction) {
        (this.modelAction as any)[key] = data[key] || modelAction[key];
      }
    },

    actionCompleted() {
      this.action_completed = random();
      // Подписка на всплывающее событие
    },

    openModalReserveAccount({ show = true, inn = '', id = '' }) {
      this.modalReserveAccount.show = show;
      this.modalReserveAccount.appInn = inn;
      this.modalReserveAccount.appId = id;
    },

    openModalReserveCurrencyAccount({ show = true, inn = '', id = '' }) {
      this.reserveCurrencyAccount.show = show;
      this.reserveCurrencyAccount.appInn = inn;
      this.reserveCurrencyAccount.appId = id;
    },

    openModalCurrencyTransitAccount({ show = true, inn = '', id = '' }) {
      this.currencyTransitAccount.show = show;
      this.currencyTransitAccount.appInn = inn;
      this.currencyTransitAccount.appId = id;
    },

    setRedirectAppActive(payload: boolean) {
      this.isRedirect = payload;
    },

    ACQUIRE({ appId }: { appId: string }) {
      const mainStore = useMainStore();

      axios.get(mainStore.getUri.actionsWork(appId)).then(() => {
        this.actionCompleted();
      });
    },

    PASS({ appId }: { appId: string }) {
      const mainStore = useMainStore();

      axios.delete(mainStore.getUri.actionsWork(appId)).then(() => {
        this.actionCompleted();
      });
    },

    REFUSE({
      appId,
      actionId,
      title = '',
      isRedirect,
    }: {
      appId: string;
      actionId: string;
      title?: string;
      isRedirect: boolean;
    }) {
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'nextStatus',
        appId,
        actionId,
        title: title.endsWith('?') ? title : `${title}?`,
        titleComment: 'Комментарий',
        titleButton: 'Подтвердить',
      } as IAction);
    },

    REFUSE_COMMENT({
      appId,
      actionId,
      title = '',
      isRedirect,
    }: {
      appId: string;
      actionId: string;
      title?: string;
      isRedirect: boolean;
    }) {
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'nextStatus',
        appId,
        actionId,
        title: title.endsWith('?') ? title : `${title}?`,
        titleComment: 'Причина отказа',
        titleButton: 'Отказать',
        requiredComment: true,
      } as IAction);
    },

    OK({ appId, actionId, isRedirect }: { appId: string; actionId: string; isRedirect: boolean }) {
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'nextStatus',
        appId,
        actionId,
        title: 'Подтвердить переход?',
        titleComment: 'Комментарий',
        titleButton: 'Подтвердить',
      } as IAction);
    },

    OPEN_ACCOUNT({ appId, actionId, isRedirect }: { appId: string; actionId: string; isRedirect: boolean }) {
      const appStore = useAppStore();
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'openAccount',
        appId,
        actionId,
        title: appStore.account?.name ? appStore.account.name + '?' : '',
        titleComment: '',
        titleButton: 'Подтвердить',
      } as IAction);
    },

    OPEN_ACCOUNT_WITHOUT_INTEGRATION({
      appId,
      actionId,
      isRedirect,
    }: {
      appId: string;
      actionId: string;
      isRedirect: boolean;
    }) {
      const appStore = useAppStore();
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'openAccount',
        appId,
        actionId,
        title: appStore.accountWithoutIntegration?.name ? appStore.accountWithoutIntegration.name + '?' : '',
        titleComment: '',
        titleButton: 'Подтвердить',
      } as IAction);
    },

    SIGN({ appId, actionId, isRedirect }: { appId: string; actionId: string; isRedirect: boolean }) {
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'nextStatus',
        appId,
        actionId,
        title: 'Подписать заявку?',
        titleComment: 'Комментарий',
        titleButton: 'Подписать',
      } as IAction);
    },

    SIGNING_AFTER_VERIFICATION({
      appId,
      actionId,
      isRedirect,
    }: {
      appId: string;
      actionId: string;
      isRedirect: boolean;
    }) {
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'nextStatus',
        appId,
        actionId,
        title: 'Подписать заявку?',
        titleComment: 'Комментарий',
        titleButton: 'Подписать',
      } as IAction);
    },

    NEXT_STATUS_AFTER_VERIFICATION({
      appId,
      actionId,
      isRedirect,
    }: {
      appId: string;
      actionId: string;
      isRedirect: boolean;
    }) {
      this.setRedirectAppActive(isRedirect);
      this.setActionModal({
        show: true,
        method: 'nextStatus',
        appId,
        actionId,
        title: 'Подтвердить переход?',
        titleComment: 'Комментарий',
        titleButton: 'Подтвердить',
      } as IAction);
    },

    async nextStatus() {
      const appStore = useAppStore();
      const bankStore = useBankStore();

      try {
        await appStore.transferAction(this.modelAction.actionId, appStore.appId, this.modelAction.comment);
        this.modelAction.disabled = false;
        this.actionCompleted();
        this.setActionModal();
        await bankStore.getStatusCount();
      } catch (e: any) {
        if (e.response && e.response.status === 409) {
          this.modelAction.disabled = true;
        }
        throw e;
      }
    },

    async openAccount() {
      const appStore = useAppStore();
      const bankStore = useBankStore();

      try {
        await appStore.transferAction();
        this.modelAction.disabled = false;
        this.actionCompleted();
        this.setActionModal();
        await bankStore.getStatusCount();
      } catch (e: any) {
        if (e.response && e.response.status === 409) {
          this.modelAction.disabled = true;
        }
        throw e;
      }
    },

    _empty() {},
  },
});

import { useMainStore } from './mainStore';
import { useAppStore } from './app';
import { useBankStore } from './bank';
import { random } from '@/plugins/utils';
export default useActionsStore;
